import React, { useState, useEffect } from 'react';
import Select from 'react-select';
import { useNavigate, Link } from 'react-router-dom';
import TicketDetails from './TicketDetails';
import NavBar from './NavBar';
import './AgentAssist.css';
import wiproLogo from './assets/wipro.png';
import { FaTicketAlt, FaClock, FaCheckCircle, FaExclamationTriangle, FaSearch, FaPause, FaPlayCircle, FaFileAlt, FaSort, FaSortUp, FaSortDown } from 'react-icons/fa';

// App version - increment this to force cache clear on all clients
const APP_VERSION = '1.3.0'; // Updated to clear cache for priority fix

const fetchData = async () => {
  const API_URL = "/api/tickets"
  const response = await fetch(API_URL, { method: "GET" })
  return response
}

// Priority mapping: handles both numeric (1,2,3,4) and string formats ("1 - Critical")
// ServiceNow priority: 1=Critical (highest), 2=High, 3=Moderate, 4=Low (lowest)
const getPriorityLabel = (priority) => {
  if (!priority) return 'N/A';
  
  // If already a formatted string from API (e.g., "1 - Critical"), extract the text part
  if (typeof priority === 'string' && priority.includes(' - ')) {
    return priority.split(' - ')[1]; // Returns "Critical", "High", "Moderate", "Low"
  }
  
  // If numeric, map to label (ServiceNow standard)
  const mapping = {
    '1': 'Critical',
    '2': 'High',
    '3': 'Moderate',
    '4': 'Low'
  };
  return mapping[String(priority)] || priority;
};

// Extract numeric priority for sorting
// Lower number = higher priority in ServiceNow (1=Critical is most urgent)
const getPriorityNumber = (priority) => {
  if (!priority) return 0;
  
  // If string format "1 - Critical", extract the number
  if (typeof priority === 'string' && priority.includes(' - ')) {
    return parseInt(priority.split(' - ')[0]);
  }
  
  // If already numeric or string number
  return parseInt(priority) || 0;
};

const AgentAssist = ({ userName, userEmail, onLogout }) => {
  const [filters, setFilters] = useState({
    ticketId: [],
    assignmentGroup: [],
    assignedTo: [],
    status: [],
    shortDescription: [],
    priority: [],
    dateRange: { start: '', end: '' }
  });

  const [selectedTicket, setSelectedTicket] = useState(null);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showDateRange, setShowDateRange] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const navigate = useNavigate();

  // Load saved filters from localStorage on mount
  useEffect(() => {
    const savedFilters = localStorage.getItem('agentAssistFilters');
    if (savedFilters) {
      try {
        const parsed = JSON.parse(savedFilters);
        setFilters(parsed);
        console.log('Loaded saved filters:', parsed);
      } catch (error) {
        console.error('Error loading saved filters:', error);
      }
    }
  }, []);

  // Load cached data on mount
  useEffect(() => {
    // Check app version and clear cache if version changed
    const cachedVersion = localStorage.getItem('appVersion');
    if (cachedVersion !== APP_VERSION) {
      console.log('App version changed, clearing cache...');
      localStorage.removeItem('agentAssistTicketsCache');
      localStorage.removeItem('agentAssistFilters');
      localStorage.setItem('appVersion', APP_VERSION);
      // Force fetch fresh data
      handleSyncData();
      return;
    }
    
    const cachedData = localStorage.getItem('agentAssistTicketsCache');
    if (cachedData) {
      try {
        const cache = JSON.parse(cachedData);
        // Check if cache is newer than 20 minutes (1200000 ms)
        const cacheAge = Date.now() - (cache.timestamp || 0);
        if (cacheAge > 1200000) {
          console.log('Cache expired (>20min), fetching fresh data...');
          handleSyncData();
        } else {
          // Use cached data if it has tickets array, otherwise use it directly
          const tickets = cache.tickets || cache;
          setData(tickets);
          console.log('Loaded cached tickets:', tickets.length);
        }
      } catch (error) {
        console.error('Error loading cached tickets:', error);
        // If cache is corrupted, fetch fresh data
        handleSyncData();
      }
    } else {
      // No cache, fetch data on first load
      handleSyncData();
    }
  }, []);

  // Function to fetch fresh data from API
  const handleSyncData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetchData();
      console.log('Raw response:', response);
      const result = await response.json();
      console.log('Parsed result:', result);
      console.log('Result array:', result.result);
      
      const tickets = (result.result || []).map(item => {
        console.log('=== TICKET PRIORITY DEBUG ===');
        console.log('Ticket:', item.number);
        console.log('Raw API priority:', item.priority, 'Type:', typeof item.priority);
        console.log('============================');
        
        return {
          ticketId: item.number,
          shortDescription: item.short_description,
          assignmentGroup: item.assignment_group,
          receivedOn: item.opened_at,
          assignedTo: item.assigned_to || '',
          status: item.state,
          item: item.cmdb_ci,
          category: item.category || '',
          priority: item.priority, // Use raw ServiceNow value (e.g., "1 - Critical")
          action: 'Review',
          // Keep raw field names for ScreenTimeAnalytics compatibility
          number: item.number,
          short_description: item.short_description,
          assignment_group: item.assignment_group,
          opened_at: item.opened_at,
          assigned_to: item.assigned_to || '',
          resolved_at: item.resolved_at,
          updated_at: item.updated_at,
          state: item.state,
          cmdb_ci: item.cmdb_ci
        };
      });
      console.log('Mapped tickets:', tickets);
      console.log('Number of tickets:', tickets.length);
      setData(tickets);
      
      // Cache the data with timestamp
      const cacheData = {
        tickets: tickets,
        timestamp: Date.now()
      };
      localStorage.setItem('agentAssistTicketsCache', JSON.stringify(cacheData));
      console.log('Tickets cached successfully with timestamp');
    } catch (error) {
      console.error('Error fetching tickets:', error);
      setError(error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (selectedOptions, actionMeta) => {
    const newFilters = {
      ...filters,
      [actionMeta.name]: selectedOptions ? selectedOptions.map(option => option.value) : []
    };
    setFilters(newFilters);
    // Save filters to localStorage
    localStorage.setItem('agentAssistFilters', JSON.stringify(newFilters));
  };

  const handleClearFilters = () => {
    const emptyFilters = {
      ticketId: [],
      assignmentGroup: [],
      assignedTo: [],
      status: [],
      shortDescription: [],
      priority: []
    };
    setFilters(emptyFilters);
    setSearchTerm('');
    localStorage.removeItem('agentAssistFilters');
  };

  const handleTicketClick = (ticket) => {
    setSelectedTicket(ticket);
  };

  const handleCloseDetails = () => {
    setSelectedTicket(null);
  };

  const handleExportToExcel = () => {
    // Use filteredData (already filtered by current filters and search)
    const dataToExport = filteredData.map(ticket => ({
      'Ticket ID': ticket.ticketId,
      'Short Description': ticket.shortDescription,
      'Assignment Group': ticket.assignmentGroup,
      'Received On': ticket.receivedOn,
      'Assigned To': ticket.assignedTo,
      'State': ticket.status,
      'Priority': ticket.priority,
      'Category': ticket.category || ''
    }));

    // Convert to CSV format
    const headers = Object.keys(dataToExport[0] || {});
    const csvContent = [
      headers.join(','),
      ...dataToExport.map(row => 
        headers.map(header => {
          const value = String(row[header] || '');
          // Escape quotes and wrap in quotes if contains comma
          return value.includes(',') || value.includes('"') 
            ? `"${value.replace(/"/g, '""')}"` 
            : value;
        }).join(',')
      )
    ].join('\n');

    // Create blob and download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    const timestamp = new Date().toISOString().split('T')[0];
    link.setAttribute('href', url);
    link.setAttribute('download', `tickets_export_${timestamp}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const filteredData = data.filter(agent => {
    // Apply dropdown filters
    const matchesFilters = (
      (filters.ticketId.length === 0 || filters.ticketId.includes(agent.ticketId)) &&
      (filters.assignmentGroup.length === 0 || filters.assignmentGroup.includes(agent.assignmentGroup)) &&
      (filters.assignedTo.length === 0 || filters.assignedTo.includes(agent.assignedTo)) &&
      (filters.status.length === 0 || filters.status.includes(agent.status)) &&
      (filters.shortDescription.length === 0 || filters.shortDescription.some(desc => agent.shortDescription?.includes(desc))) &&
      (filters.priority.length === 0 || filters.priority.includes(agent.priority))
    );

    // Apply date range filter
    const matchesDateRange = (() => {
      if (!filters.dateRange.start && !filters.dateRange.end) return true;
      if (!agent.receivedOn) return false;
      
      const ticketDate = new Date(agent.receivedOn);
      const startDate = filters.dateRange.start ? new Date(filters.dateRange.start) : null;
      const endDate = filters.dateRange.end ? new Date(filters.dateRange.end) : null;
      
      if (startDate && ticketDate < startDate) return false;
      if (endDate && ticketDate > endDate) return false;
      return true;
    })();

    // Apply search term
    const matchesSearch = searchTerm === '' || 
      agent.ticketId?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      agent.shortDescription?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      agent.assignmentGroup?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      agent.assignedTo?.toLowerCase().includes(searchTerm.toLowerCase());

    return matchesFilters && matchesSearch && matchesDateRange;
  });

  // Sorting logic
  const sortedData = [...filteredData].sort((a, b) => {
    if (!sortConfig.key) return 0;
    
    let aValue = a[sortConfig.key];
    let bValue = b[sortConfig.key];
    
    // Special handling for priority - use numeric value
    if (sortConfig.key === 'priority') {
      aValue = a.priorityNum || 0;
      bValue = b.priorityNum || 0;
    }
    
    // Handle null/undefined values
    if (aValue === null || aValue === undefined) return 1;
    if (bValue === null || bValue === undefined) return -1;
    
    // String comparison (case-insensitive)
    if (typeof aValue === 'string' && typeof bValue === 'string') {
      aValue = aValue.toLowerCase();
      bValue = bValue.toLowerCase();
    }
    
    if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
    return 0;
  });

  // Pagination calculations
  const totalPages = Math.ceil(sortedData.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentTickets = sortedData.slice(indexOfFirstItem, indexOfLastItem);

  // Reset to page 1 when filters or search changes (but NOT when sorting)
  useEffect(() => {
    setCurrentPage(1);
  }, [filters, searchTerm]); // sortConfig intentionally excluded

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Calculate statistics based on filtered data
  const stats = {
    total: filteredData.length,
    inProgress: filteredData.filter(t => t.status?.toLowerCase().includes('in progress') || t.status?.toLowerCase().includes('work in progress')).length,
    onHold: filteredData.filter(t => t.status?.toLowerCase().includes('on hold') || t.status?.toLowerCase().includes('hold') || t.status?.toLowerCase().includes('pending')).length,
    new: filteredData.filter(t => (t.status?.toLowerCase().includes('new') || t.status?.toLowerCase().includes('open')) && !t.status?.toLowerCase().includes('in progress')).length
  };

  // Handle stat card clicks to filter by status
  const handleStatClick = (statusType) => {
    let statusValues = [];
    
    switch(statusType) {
      case 'inProgress':
        // Find all unique status values that match "in progress"
        statusValues = [...new Set(data
          .filter(t => t.status?.toLowerCase().includes('in progress') || t.status?.toLowerCase().includes('work in progress'))
          .map(t => t.status))];
        break;
      case 'onHold':
        // Find all unique status values that match "on hold"
        statusValues = [...new Set(data
          .filter(t => t.status?.toLowerCase().includes('on hold') || t.status?.toLowerCase().includes('hold') || t.status?.toLowerCase().includes('pending'))
          .map(t => t.status))];
        break;
      case 'new':
        // Find all unique status values that match "new"
        statusValues = [...new Set(data
          .filter(t => (t.status?.toLowerCase().includes('new') || t.status?.toLowerCase().includes('open')) && !t.status?.toLowerCase().includes('in progress'))
          .map(t => t.status))];
        break;
      case 'total':
        // Clear status filter to show all
        statusValues = [];
        break;
    }
    
    const newFilters = {
      ...filters,
      status: statusValues
    };
    setFilters(newFilters);
    localStorage.setItem('agentAssistFilters', JSON.stringify(newFilters));
  };

  const getOptions = (key) => {
    if (!Array.isArray(data)) return [];
    const values = data.map(agent => agent[key]).filter(val => typeof val !== 'undefined' && val !== null);
    const unique = [...new Set(values)];
    return unique.map(option => ({ value: option, label: option }));
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
    // Note: currentPage is NOT reset when sorting - user stays on same page number
  };

  if (loading) return (
    <div className="loading-container">
      <div className="loading-spinner"></div>
      <p>Loading tickets...</p>
    </div>
  );
  
  if (error) return (
    <div className="error-container">
      <FaExclamationTriangle size={48} color="#C8102E" />
      <p>Error loading tickets: {error}</p>
    </div>
  );

  return (
    <div className="dashboard-wrapper">
      <NavBar userName={userName} userEmail={userEmail} onLogout={onLogout} />
      
      <div className="dashboard-content">
        {/* Dashboard Header */}
        <div className="dashboard-header">
          <div>
            <h1 className="dashboard-title">Dashboard</h1>
            <p className="dashboard-subtitle">Manage and track all support tickets</p>
          </div>
          <div style={{ display: 'flex', gap: '12px' }}>
            <button onClick={handleSyncData} className="sync-data-btn" disabled={loading}>
              🔄 {loading ? 'Syncing...' : 'Sync Data'}
            </button>
            <Link to="/analytics" className="analytics-link">
              📊 View Analytics
            </Link>
          </div>
        </div>

        {/* Statistics Cards */}
        <div className="stats-grid">
          <div className="stat-card stat-total" onClick={() => handleStatClick('all')} style={{ cursor: 'pointer' }}>
            <div className="stat-icon">
              <FaTicketAlt size={12} />
            </div>
            <div className="stat-content">
              <div className="stat-value">{stats.total}</div>
              <div className="stat-label">Total Tickets</div>
            </div>
          </div>

          <div className="stat-card stat-inprogress" onClick={() => handleStatClick('inProgress')} style={{ cursor: 'pointer' }}>
            <div className="stat-icon">
              <FaPlayCircle size={12} />
            </div>
            <div className="stat-content">
              <div className="stat-value">{stats.inProgress}</div>
              <div className="stat-label">In Progress</div>
            </div>
          </div>

          <div className="stat-card stat-onhold" onClick={() => handleStatClick('onHold')} style={{ cursor: 'pointer' }}>
            <div className="stat-icon">
              <FaPause size={12} />
            </div>
            <div className="stat-content">
              <div className="stat-value">{stats.onHold}</div>
              <div className="stat-label">On Hold</div>
            </div>
          </div>

          <div className="stat-card stat-new" onClick={() => handleStatClick('new')} style={{ cursor: 'pointer' }}>
            <div className="stat-icon">
              <FaFileAlt size={12} />
            </div>
            <div className="stat-content">
              <div className="stat-value">{stats.new}</div>
              <div className="stat-label">New</div>
            </div>
          </div>
        </div>

        {/* Search and Filter Bar Container */}
        <div className="search-filter-container">
          {/* Search Bar */}
          <div className="search-bar">
            <FaSearch className="search-icon" />
            <input
              type="text"
              placeholder="Search ticket by ID, description or assignment..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
            {searchTerm && (
              <button 
                className="search-clear"
                onClick={() => setSearchTerm('')}
              >
                ✕
              </button>
            )}
          </div>

          {/* Centralized Filter Bar */}
          <div className="filter-bar">
            <div className="filter-label">Filter By:</div>
            
            <div className="filter-group">
              <Select
                isMulti
                name="status"
                options={getOptions('status')}
                value={getOptions('status').filter(opt => filters.status.includes(opt.value))}
                className="filter-select"
                classNamePrefix="select"
                placeholder="STATUS"
                onChange={handleFilterChange}
              />
            </div>

            <div className="filter-group">
              <Select
                isMulti
                name="priority"
                options={getOptions('priority')}
                value={getOptions('priority').filter(opt => filters.priority.includes(opt.value))}
                className="filter-select"
                classNamePrefix="select"
                placeholder="PRIORITY"
                onChange={handleFilterChange}
              />
            </div>

            <div className="filter-group">
              <Select
                isMulti
                name="assignmentGroup"
                options={getOptions('assignmentGroup')}
                value={getOptions('assignmentGroup').filter(opt => filters.assignmentGroup.includes(opt.value))}
                className="filter-select"
                classNamePrefix="select"
                placeholder="GROUP"
                onChange={handleFilterChange}
              />
            </div>

            <div className="filter-group date-filter-group">
              <button 
                className="date-range-toggle"
                onClick={() => setShowDateRange(!showDateRange)}
                title="Filter by date range"
              >
                📅
              </button>
              {showDateRange && (
                <div className="date-range-popup">
                  <input
                    type="date"
                    value={filters.dateRange.start}
                    onChange={(e) => setFilters({ ...filters, dateRange: { ...filters.dateRange, start: e.target.value } })}
                    className="date-input-compact"
                  />
                  <span style={{ fontSize: '11px', color: '#718096' }}>to</span>
                  <input
                    type="date"
                    value={filters.dateRange.end}
                    onChange={(e) => setFilters({ ...filters, dateRange: { ...filters.dateRange, end: e.target.value } })}
                    className="date-input-compact"
                  />
                </div>
              )}
            </div>

            <div className="filter-actions">
              <button className="configure-btn">
                ⚙️ CONFIGURE
              </button>
              <button className="export-btn" onClick={handleExportToExcel}>
                📥 EXPORT
              </button>
            </div>
          </div>
        </div>

        {/* Tickets Table */}
        <div className="table-container">
          <div className="table-scroll">
      <table className="agent-table">
        <thead>
          <tr>
            <th className="checkbox-col">
              <input type="checkbox" />
            </th>
            <th onClick={() => handleSort('ticketId')} style={{ cursor: 'pointer' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                Ticket ID# 
                {sortConfig.key === 'ticketId' ? (sortConfig.direction === 'asc' ? <FaSortUp /> : <FaSortDown />) : <FaSort opacity={0.3} />}
              </div>
            </th>
            <th onClick={() => handleSort('shortDescription')} style={{ cursor: 'pointer' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                Short Description
                {sortConfig.key === 'shortDescription' ? (sortConfig.direction === 'asc' ? <FaSortUp /> : <FaSortDown />) : <FaSort opacity={0.3} />}
              </div>
            </th>
            <th onClick={() => handleSort('assignmentGroup')} style={{ cursor: 'pointer' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                Assignment Group
                {sortConfig.key === 'assignmentGroup' ? (sortConfig.direction === 'asc' ? <FaSortUp /> : <FaSortDown />) : <FaSort opacity={0.3} />}
              </div>
            </th>
            <th onClick={() => handleSort('receivedOn')} style={{ cursor: 'pointer' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                Received On
                {sortConfig.key === 'receivedOn' ? (sortConfig.direction === 'asc' ? <FaSortUp /> : <FaSortDown />) : <FaSort opacity={0.3} />}
              </div>
            </th>
            <th onClick={() => handleSort('assignedTo')} style={{ cursor: 'pointer' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                Assigned To
                {sortConfig.key === 'assignedTo' ? (sortConfig.direction === 'asc' ? <FaSortUp /> : <FaSortDown />) : <FaSort opacity={0.3} />}
              </div>
            </th>
            <th>State</th>
            <th onClick={() => handleSort('priority')} style={{ cursor: 'pointer' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                Priority
                {sortConfig.key === 'priority' ? (sortConfig.direction === 'asc' ? <FaSortUp /> : <FaSortDown />) : <FaSort opacity={0.3} />}
              </div>
            </th>
            <th>Category</th>
            <th>Action</th>
          </tr>
        </thead>
        <tbody>
          {currentTickets.map(agent => (
            <tr key={agent.ticketId} onClick={() => handleTicketClick(agent)}>
              <td className="checkbox-col">
                <input type="checkbox" onClick={(e) => e.stopPropagation()} />
              </td>
              <td>{agent.ticketId}</td>
              <td>{agent.shortDescription}</td>
              <td>{agent.assignmentGroup}</td>
              <td>{agent.receivedOn}</td>
              <td>{agent.assignedTo}</td>
              <td>{agent.status}</td>
              <td>{agent.priority}</td>
              <td>{agent.category || ''}</td>
              <td>
                <button
                  className="process-button"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/process/${agent.ticketId}`);
                  }}
                >
                  PROCESS
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
          </div>

          {/* Pagination Controls */}
          {totalPages > 1 && (
            <div className="pagination-container">
              <div className="pagination-info">
                Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, filteredData.length)} of {filteredData.length} tickets
              </div>
              <div className="pagination-buttons">
                <button 
                  className="pagination-btn"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  Previous
                </button>
                
                {[...Array(totalPages)].map((_, index) => {
                  const pageNumber = index + 1;
                  // Show first page, last page, current page, and pages around current
                  if (
                    pageNumber === 1 ||
                    pageNumber === totalPages ||
                    (pageNumber >= currentPage - 1 && pageNumber <= currentPage + 1)
                  ) {
                    return (
                      <button
                        key={pageNumber}
                        className={`pagination-btn ${currentPage === pageNumber ? 'active' : ''}`}
                        onClick={() => handlePageChange(pageNumber)}
                      >
                        {pageNumber}
                      </button>
                    );
                  } else if (
                    pageNumber === currentPage - 2 ||
                    pageNumber === currentPage + 2
                  ) {
                    return <span key={pageNumber} className="pagination-dots">...</span>;
                  }
                  return null;
                })}
                
                <button 
                  className="pagination-btn"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
      
      {/* Bottom Bar */}
      <div className="login-bottom-bar">
        <div className="bottom-bar-content">
          <img src={wiproLogo} alt="Wipro" className="bottom-bar-wipro-logo" />
          <p className="bottom-bar-copyright">Copyright © 2026 Wipro LTD. All rights reserved.</p>
        </div>
      </div>
      
      {selectedTicket && <TicketDetails ticket={selectedTicket} onClose={handleCloseDetails} />}
    </div>
  );
};

export default AgentAssist;